import { createTheme } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';

import type {
  ChartPaletteToken,
  IconTintPaletteToken,
  NavigationPaletteToken,
  SemanticPaletteToken,
  SurfacePaletteToken,
} from './types';

export interface AppSemanticPalette {
  readonly navigation: NavigationPaletteToken;
  readonly financialPositive: SemanticPaletteToken;
  readonly financialNegative: SemanticPaletteToken;
  readonly warning: SemanticPaletteToken;
  readonly critical: SemanticPaletteToken;
  readonly mockData: SemanticPaletteToken;
  readonly surface: SurfacePaletteToken;
  readonly chart: ChartPaletteToken;
  readonly iconTint: IconTintPaletteToken;
}

export const semanticPaletteDefaults: Readonly<{
  light: AppSemanticPalette;
  dark: AppSemanticPalette;
}> = {
  light: {
    navigation: {
      background: '#ffffff',
      divider: '#dbe6eb',
      selected: '#eaf3fd',
      text: '#1b2124',
    },
    financialPositive: token('#099f69', '#e6f5f0', '#066f49', '#ffffff'),
    financialNegative: token('#d02241', '#f9e2e6', '#91182d', '#ffffff'),
    warning: token('#f68d2a', '#feefe1', '#ac631d', '#1b2124'),
    critical: token('#b11d37', '#f2c1ca', '#721324', '#ffffff'),
    mockData: token('#a641fa', '#f2e4fe', '#742daf', '#ffffff'),
    surface: { subtle: '#f4f7fa', sunken: '#eef2f6' },
    chart: {
      // Validated with dataviz scripts/validate_palette.js --mode light --surface #ffffff.
      series: ['#3385f0', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
      // A ~10% wash of series 1, per the area-fill mark spec.
      areaFill: 'rgba(51, 133, 240, 0.1)',
      grid: '#eef2f6',
      axisLabel: '#4d595e',
      other: '#8a979d',
    },
    iconTint: {
      blue: { background: '#e8f1fe', foreground: '#1d63c4' },
      orange: { background: '#fdefe6', foreground: '#b8501f' },
      teal: { background: '#e4f6ef', foreground: '#0b7d55' },
      amber: { background: '#fdf3e0', foreground: '#9a6a00' },
      violet: { background: '#efe9fc', foreground: '#5340a8' },
    },
  },
  dark: {
    navigation: {
      background: '#111417',
      divider: '#262d30',
      selected: '#143560',
      text: '#f7fafc',
    },
    financialPositive: token('#35b084', '#04402a', '#62c29f', '#06080a'),
    financialNegative: token('#e17286', '#530e1a', '#e99aa8', '#06080a'),
    warning: token('#f8a250', '#623811', '#f9b677', '#06080a'),
    critical: token('#d84a63', '#721324', '#e17286', '#ffffff'),
    mockData: token('#b663fb', '#421a64', '#c686fc', '#06080a'),
    surface: { subtle: '#171b1f', sunken: '#0f1215' },
    chart: {
      // Validated with dataviz scripts/validate_palette.js --mode dark --surface #111417.
      series: ['#4a90f0', '#d95926', '#199e70', '#c98500', '#d55181'],
      areaFill: 'rgba(74, 144, 240, 0.12)',
      grid: '#262d30',
      axisLabel: '#9caeb8',
      other: '#6d7d85',
    },
    iconTint: {
      blue: { background: '#152a44', foreground: '#7fb2f7' },
      orange: { background: '#3a2216', foreground: '#f0a077' },
      teal: { background: '#0e2f24', foreground: '#5cc4a0' },
      amber: { background: '#33270d', foreground: '#e0b45a' },
      violet: { background: '#241d3d', foreground: '#a99cf0' },
    },
  },
};

export function createAppTheme() {
  return createTheme({
    colorSchemes: {
      dark: {
        palette: {
          background: { default: '#06080a', paper: '#111417' },
          chart: semanticPaletteDefaults.dark.chart,
          critical: semanticPaletteDefaults.dark.critical,
          error: semanticPaletteDefaults.dark.financialNegative,
          financialNegative: semanticPaletteDefaults.dark.financialNegative,
          financialPositive: semanticPaletteDefaults.dark.financialPositive,
          iconTint: semanticPaletteDefaults.dark.iconTint,
          mockData: semanticPaletteDefaults.dark.mockData,
          navigation: semanticPaletteDefaults.dark.navigation,
          primary: { main: '#589bf3' },
          success: semanticPaletteDefaults.dark.financialPositive,
          surface: semanticPaletteDefaults.dark.surface,
          text: { primary: '#f7fafc', secondary: '#9caeb8' },
          warning: semanticPaletteDefaults.dark.warning,
        },
      },
      light: {
        palette: {
          background: { default: '#f7fafc', paper: '#ffffff' },
          chart: semanticPaletteDefaults.light.chart,
          critical: semanticPaletteDefaults.light.critical,
          error: semanticPaletteDefaults.light.financialNegative,
          financialNegative: semanticPaletteDefaults.light.financialNegative,
          financialPositive: semanticPaletteDefaults.light.financialPositive,
          iconTint: semanticPaletteDefaults.light.iconTint,
          mockData: semanticPaletteDefaults.light.mockData,
          navigation: semanticPaletteDefaults.light.navigation,
          primary: { main: '#3385f0' },
          success: semanticPaletteDefaults.light.financialPositive,
          surface: semanticPaletteDefaults.light.surface,
          text: { primary: '#1b2124', secondary: '#4d595e' },
          warning: semanticPaletteDefaults.light.warning,
        },
      },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8, textTransform: 'none' } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
          sizeSmall: { height: 24 },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '*': { scrollbarWidth: 'thin' },
          body: { fontVariantLigatures: 'none' },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          columnHeaderHeight: 48,
          disableRowSelectionOnClick: true,
        },
        styleOverrides: {
          root: { border: 0 },
          columnHeader: { outline: 'none' },
          columnHeaders: ({ theme }) => ({
            backgroundColor: theme.vars.palette.surface.subtle,
          }),
          columnSeparator: { display: 'none' },
          cell: { fontVariantNumeric: 'tabular-nums' },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: theme.vars.palette.navigation.selected,
              color: theme.vars.palette.primary.main,
              fontWeight: 600,
              '& .MuiListItemIcon-root': { color: theme.vars.palette.primary.main },
              '& .MuiListItemText-primary': { fontWeight: 600 },
            },
            borderRadius: 8,
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { color: 'inherit', minWidth: 34 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            '&.MuiPaper-outlined': { borderColor: theme.vars.palette.divider },
          }),
          rounded: { borderRadius: 12 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { fontWeight: 600, minHeight: 44, textTransform: 'none' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.vars.palette.divider,
            fontVariantNumeric: 'tabular-nums',
          }),
          head: ({ theme }) => ({
            backgroundColor: theme.vars.palette.surface.subtle,
            color: theme.vars.palette.text.secondary,
            fontWeight: 600,
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600, textTransform: 'none' },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontSize: '0.75rem' },
        },
      },
    },
    cssVariables: {
      colorSchemeSelector: 'data-aurora-color-scheme',
      cssVarPrefix: 'aurora',
    },
    shape: { borderRadius: 12 },
    typography: {
      button: { fontWeight: 600, textTransform: 'none' },
      fontFamily: [
        'Inter Variable',
        'Inter',
        'IBM Plex Sans Thai',
        'ui-sans-serif',
        'system-ui',
        'sans-serif',
      ].join(','),
      fontWeightBold: 700,
      fontWeightMedium: 500,
      fontWeightRegular: 400,
      h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.25 },
      h2: { fontSize: '1.625rem', fontWeight: 600, lineHeight: 1.3 },
      h3: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.35 },
      h4: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.45 },
      h6: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.5 },
      body1: { fontSize: '0.875rem', lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5 },
      subtitle1: { fontSize: '0.875rem', fontWeight: 600 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
    },
  });
}

export const appTheme = createAppTheme();

function token(
  main: string,
  light: string,
  dark: string,
  contrastText: string,
): SemanticPaletteToken {
  return { main, light, dark, contrastText };
}
